<template>
  <div :class="avatarClasses" :style="avatarStyles">
    <img
      v-if="src && !imageError"
      :src="src"
      :alt="alt"
      class="nvx-avatar__image"
      @error="handleImageError"
    />
    <div v-else class="nvx-avatar__fallback">
      <NvxIcon v-if="fallbackIcon" :name="fallbackIcon" :size="iconSize" />
      <span v-else class="nvx-avatar__initials">{{ initials }}</span>
    </div>
    
    <div v-if="status" :class="statusClasses" :title="status" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import NvxIcon from './NvxIcon.vue'

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
type AvatarStatus = 'online' | 'offline' | 'away' | 'busy'

interface Props {
  src?: string
  alt?: string
  size?: AvatarSize
  status?: AvatarStatus
  fallbackIcon?: string
  name?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  alt: ''
})

const imageError = ref(false)

// Reset error state when src changes to allow new image to attempt loading
watch(() => props.src, () => {
  imageError.value = false
})

const avatarClasses = computed(() => [
  'nvx-avatar',
  `nvx-avatar--${props.size}`,
  {
    'nvx-avatar--with-status': props.status
  }
])

const avatarStyles = computed(() => {
  const sizeMap = {
    xs: '24px',
    sm: '32px',
    md: '40px',
    lg: '48px',
    xl: '64px',
    '2xl': '80px'
  }
  
  return {
    '--avatar-size': sizeMap[props.size]
  }
})

const statusClasses = computed(() => [
  'nvx-avatar__status',
  `nvx-avatar__status--${props.status}`
])

const iconSize = computed(() => {
  const sizeMap = {
    xs: 'xs',
    sm: 'sm',
    md: 'sm',
    lg: 'md',
    xl: 'lg',
    '2xl': 'xl'
  }
  return sizeMap[props.size]
})

const initials = computed(() => {
  if (!props.name) return '?'
  
  return props.name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)
})

const handleImageError = () => {
  imageError.value = true
}
</script>

<style lang="scss" scoped>
.nvx-avatar {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--avatar-size);
  height: var(--avatar-size);
  border-radius: var(--nvx-border-radius-full);
  background-color: var(--nvx-color-background-secondary);
  overflow: hidden;
  flex-shrink: 0;

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background-color: var(--nvx-color-accent-primary);
    color: var(--nvx-color-text-primary);
  }

  &__initials {
    font-weight: var(--nvx-font-weight-semibold);
    color: var(--nvx-color-text-primary);
  }

  &__status {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 25%;
    height: 25%;
    border-radius: var(--nvx-border-radius-full);
    border: 2px solid var(--nvx-color-background-primary);

    &--online {
      background-color: var(--nvx-color-status-success);
    }

    &--offline {
      background-color: var(--nvx-color-text-tertiary);
    }

    &--away {
      background-color: var(--nvx-color-status-warning);
    }

    &--busy {
      background-color: var(--nvx-color-status-error);
    }
  }

  // Size-specific font sizes for initials
  &--xs &__initials {
    font-size: var(--nvx-font-size-xs);
  }

  &--sm &__initials {
    font-size: var(--nvx-font-size-sm);
  }

  &--md &__initials {
    font-size: var(--nvx-font-size-sm);
  }

  &--lg &__initials {
    font-size: var(--nvx-font-size-base);
  }

  &--xl &__initials {
    font-size: var(--nvx-font-size-lg);
  }

  &--2xl &__initials {
    font-size: var(--nvx-font-size-xl);
  }
}
</style>
